/**
 * @module ThresholdMetricRow
 * @description Phase 17 extraction of a single threshold metric row renderer for TestThresholdsSection.
 * Receives the metric item, mode flags, and change handlers needed by manual/global threshold layouts.
 */

import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { Box, TextField, Typography } from '@material-ui/core';

import { getThresholdDynamicBounds, showThresholdError } from '../../../thresholds/ThresholdValidation';

/**
 * Renders a single threshold metric row with degraded + critical inputs.
 * Used in both Global (disabled) and Manual (editable) modes.
 */
export default function ThresholdMetricRow({
    threshold,
    metricLabel,
    metricUnit,
    disabled,
    formIndex,
    onThresholdChange,
    classes,
}) {
    const unitSuffix = metricUnit ? ` (${metricUnit})` : '';

    const { degradedMin, degradedMax, excessiveMin, excessiveMax } = disabled
        ? {
              degradedMin: undefined,
              degradedMax: undefined,
              excessiveMin: undefined,
              excessiveMax: undefined,
          }
        : getThresholdDynamicBounds(threshold, threshold?.degraded, threshold?.critical);

    const errorMessage = disabled ? null : showThresholdError(threshold, threshold?.degraded, threshold?.critical);

    return (
        <Box display="flex" alignItems="flex-end" mb={1}>
            <Box className={disabled ? classes.metricName : classes.metricNameManual}>
                <Typography variant="body2">
                    {metricLabel}
                    {unitSuffix && (
                        <Typography component="span" variant="caption" color="textSecondary">
                            {unitSuffix}
                        </Typography>
                    )}
                </Typography>
            </Box>
            <Box ml={9} className={classes.metricField}>
                <TextField
                    type="number"
                    label={t`Degraded`}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    value={threshold?.degraded ?? ''}
                    error={!!errorMessage}
                    inputProps={{
                        min: degradedMin,
                        max: degradedMax,
                        step: 'any',
                    }}
                    onChange={
                        disabled ? undefined : (event) => onThresholdChange(formIndex, 'degraded', event.target.value)
                    }
                />
            </Box>
            <Box ml={9} className={classes.metricField}>
                <TextField
                    type="number"
                    label={t`Excessive`}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    value={threshold?.critical ?? ''}
                    error={!!errorMessage}
                    inputProps={{
                        min: excessiveMin,
                        max: excessiveMax,
                        step: 'any',
                    }}
                    onChange={
                        disabled ? undefined : (event) => onThresholdChange(formIndex, 'critical', event.target.value)
                    }
                />
            </Box>
            {errorMessage && (
                <Box ml={9} style={{ minWidth: 180 }}>
                    <Typography color="error" variant="caption" style={{ fontWeight: 'bold' }}>
                        {errorMessage}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

ThresholdMetricRow.propTypes = {
    threshold: PropTypes.shape({
        critical: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        degraded: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        metricIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    }),
    metricLabel: PropTypes.string,
    metricUnit: PropTypes.string,
    disabled: PropTypes.bool,
    formIndex: PropTypes.number,
    onThresholdChange: PropTypes.func,
    classes: PropTypes.shape({
        metricName: PropTypes.string.isRequired,
        metricNameManual: PropTypes.string.isRequired,
        metricField: PropTypes.string.isRequired,
    }).isRequired,
};

ThresholdMetricRow.defaultProps = {
    threshold: undefined,
    metricLabel: '',
    metricUnit: '',
    disabled: false,
    formIndex: -1,
    onThresholdChange: undefined,
};
